import { useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import { projectsData, projectCategories } from '@/mocks/home';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';
import JsonLd, { getWebPageSchema, getBreadcrumbSchema } from '@/utils/seo';

export default function ProjectsPage() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const { ref: gridRef, isVisible } = useScrollAnimation(0.03);

  useBodyScrollLock(lightboxImage !== null);

  const filteredProjects = activeCategory === 'All'
    ? projectsData
    : projectsData.filter((p) => p.category === activeCategory);

  return (
    <Layout>
      <JsonLd data={getWebPageSchema('Our Projects Portfolio | Completed Works | Infinite Consultant & Services', 'Explore our portfolio of completed property projects in Bangalore — landscaping interiors UPVC windows waterproofing branding fabrication and more showcasing quality craftsmanship.', 'https://infiniteconsultant.com/projects')} />
      <JsonLd data={getBreadcrumbSchema([{ name: 'Home', url: 'https://infiniteconsultant.com/' }, { name: 'Projects', url: 'https://infiniteconsultant.com/projects' }])} />
      {/* Hero */}
      <section className="relative h-[350px] md:h-[450px] w-full overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Impressive%20collage%20of%20completed%20Indian%20construction%20and%20renovation%20projects%20Bangalore%20modern%20buildings%20landscaping%20interior%20design%20showcasing%20quality%20Indian%20craftsmanship%20professional%20portfolio%20photography&width=1600&height=800&seq=projects-hero&orientation=landscape&nocache=true"
          alt="Our Projects"
          className="absolute inset-0 w-full h-full object-cover object-top animate-reveal"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/60 via-foreground-950/40 to-foreground-950/70" />
        <div className="absolute inset-0 flex items-end">
          <div className="w-full px-4 sm:px-6 lg:px-10 pb-10 md:pb-14">
            <div className="max-w-6xl mx-auto">
              <nav className="flex items-center gap-2 text-white/80 text-sm mb-4" aria-label="Breadcrumb">
                <Link to="/" className="hover:text-white transition-colors whitespace-nowrap cursor-pointer">Home</Link>
                <span className="text-white/50">/</span>
                <span className="text-white whitespace-nowrap">Projects</span>
              </nav>
              <h1 className="font-heading font-bold text-2xl md:text-4xl lg:text-5xl text-white mb-3" style={{ opacity: 0, animation: 'fadeUpSoft 0.7s cubic-bezier(0.16, 1, 0.3, 1) 0.1s forwards' }}>
                Our Projects
              </h1>
              <p className="text-sm md:text-lg text-white/80 max-w-2xl" style={{ opacity: 0, animation: 'fadeUpSoft 0.7s cubic-bezier(0.16, 1, 0.3, 1) 0.25s forwards' }}>
                Explore our portfolio of completed projects showcasing quality and craftsmanship across Bangalore.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Filter + Grid */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="w-full px-4 sm:px-6 lg:px-10">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-wrap justify-center gap-2 mb-10">
              {projectCategories.map((category) => (
                <button
                  key={category}
                  onClick={() => setActiveCategory(category)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    activeCategory === category
                      ? 'bg-gradient-brand text-white'
                      : 'bg-white text-foreground-700 border border-background-200/70 hover:border-primary-300 hover:text-primary-600'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>

            <div ref={gridRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {filteredProjects.map((project, index) => (
                <button
                  key={project.id}
                  onClick={() => setLightboxImage(project.image)}
                  className={`group bg-white rounded-xl overflow-hidden border border-background-200/70 hover:shadow-sm transition-all duration-500 text-left cursor-pointer ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                  style={{ transitionDelay: `${index * 0.08}s` }}
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={project.image}
                      alt={project.title}
                      loading="lazy"
                      className="w-full h-52 md:h-64 object-cover object-top group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    <div className="absolute bottom-3 left-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-white/90 flex items-center justify-center">
                          <i className="ri-zoom-in-line text-foreground-700" />
                        </div>
                        <span className="text-white text-sm font-medium">View Full Image</span>
                      </div>
                    </div>
                    <div className="absolute top-3 left-3">
                      <span className="px-2.5 py-1 rounded-md bg-white/90 backdrop-blur-sm text-xs font-medium text-foreground-800">
                        {project.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-4 md:p-5">
                    <h3 className="font-heading font-semibold text-sm md:text-base text-foreground-950">
                      {project.title}
                    </h3>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxImage && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 cursor-pointer animate-fade-in"
          onClick={() => setLightboxImage(null)}
        >
          <button
            onClick={() => setLightboxImage(null)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors cursor-pointer"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-close-line text-xl" />
            </div>
          </button>
          <img
            src={lightboxImage}
            alt="Project preview"
            className="max-w-full max-h-[90vh] rounded-xl object-contain animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </Layout>
  );
}